import moment from 'moment-timezone';

import { EndpointChangeWindow } from '@/react/portainer/environments/types';
import { TimeWindowPickerInputGroup } from '@/react/portainer/environments/common/TimeWindowPicker/TimeWindowPickerInputGroup';

import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Input } from '@@/form-components/Input';
import { SwitchField } from '@@/form-components/SwitchField';
import { TextTip } from '@@/Tip/TextTip';
import { InputList, ItemProps } from '@@/form-components/InputList';

export interface StorageClass {
  name: string;
  allowVolumeExpansion: boolean;
}

export interface IngressController {
  name: string;
}

export interface KubernetesSetupFormData {
  // Networking - Services
  useLoadBalancer: boolean;

  // Networking - Ingresses
  ingressControllers: IngressController[];
  allowNoneIngressClass: boolean;
  ingressAvailabilityPerNamespace: boolean;
  restrictStandardUserIngressW: boolean;

  // Change Window
  changeWindow: EndpointChangeWindow;

  // Resources and Metrics
  enableResourceOverCommit: boolean;
  resourceOverCommitPercentage: number;
  useServerMetrics: boolean;

  // Storage
  storageClasses: StorageClass[];
}

interface KubernetesSetupConstraintsProps {
  formData: KubernetesSetupFormData;
  onChange: <K extends keyof KubernetesSetupFormData>(
    key: K,
    value: KubernetesSetupFormData[K]
  ) => void;
  timeZone?: string;
  onChangeTimeZone?: (timeZone: string) => void;
}

export function KubernetesSetupInnerForm({
  formData,
  onChange,
  timeZone = moment.tz.guess(),
  onChangeTimeZone,
}: KubernetesSetupConstraintsProps) {
  // Ensure ingressControllers is always an array
  const ingressControllers = Array.isArray(formData.ingressControllers)
    ? formData.ingressControllers
    : [];

  // Ensure storageClasses is always an array
  const storageClasses = Array.isArray(formData.storageClasses)
    ? formData.storageClasses
    : [];

  return (
    <>
      {/* Networking - Services */}
      <FormSectionTitle>Networking - Services</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            Enabling the load balancer feature will allow users to expose
            applications they deploy over an external IP address assigned by the
            cloud provider.
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="orange" inline={false}>
            If you want to use this feature, ensure your cloud provider allows
            you to create load balancers. This may incur costs.
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Allow users to use external load balancers"
            tooltip="Allow users to use external load balancers"
            labelClass="col-sm-5 col-lg-4"
            checked={formData.useLoadBalancer || false}
            onChange={(value) => onChange('useLoadBalancer', value)}
            data-cy="load-balancer-toggle"
          />
        </div>
      </div>

      {/* Networking - Ingresses */}
      <FormSectionTitle>Networking - Ingresses</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              Enabling ingress controllers in your cluster allows them to be
              available in the Portainer UI for users to publish applications
              over HTTP/HTTPS. A controller must have a class name for it to be
              included here.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <InputList
            label="Allowed Ingress Controllers"
            value={ingressControllers}
            onChange={(value) => onChange('ingressControllers', value)}
            item={IngressControllerItem}
            itemBuilder={() => ({ name: '' })}
            addLabel="Add ingress controller"
            tooltip="The list of ingress controllers defined must be pre-installed in the cluster."
            data-cy="ingress-controllers-list"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label='Allow ingress class to be set to "none"'
            tooltip='This allows users setting up ingresses to select "none" as the ingress class.'
            labelClass="col-sm-5 col-lg-4"
            checked={formData.allowNoneIngressClass || false}
            onChange={(value) => onChange('allowNoneIngressClass', value)}
            data-cy="allow-none-ingress-class-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Configure ingress controller availability per namespace"
            tooltip="This allows an administrator to configure, in each namespace, which ingress controllers will be available for users to select when setting up ingresses for applications."
            labelClass="col-sm-5 col-lg-4"
            checked={formData.ingressAvailabilityPerNamespace || false}
            onChange={(value) =>
              onChange('ingressAvailabilityPerNamespace', value)
            }
            data-cy="ingress-availability-per-namespace-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Only allow admins to deploy ingresses"
            tooltip="Enforces only allowing admins to deploy ingresses (and disallows standard users from doing so)."
            labelClass="col-sm-5 col-lg-4"
            checked={formData.restrictStandardUserIngressW || false}
            onChange={(value) =>
              onChange('restrictStandardUserIngressW', value)
            }
            data-cy="restrict-standard-user-ingress-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              You may set up ingress defaults (hostnames and annotations) via
              Create/Edit ingress. Users may then select them via the hostname
              dropdown in Create/Edit application.
            </p>
          </TextTip>
        </div>
      </div>

      {/* Change Window Settings */}
      <FormSectionTitle>Change Window Settings</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Enable Change Window"
            tooltip="GitOps updates to stacks or applications outside the defined change window will not occur."
            labelClass="col-sm-5 col-lg-4"
            checked={formData.changeWindow?.Enabled || false}
            onChange={(value) =>
              onChange('changeWindow', {
                ...(formData.changeWindow || {
                  Enabled: false,
                  StartTime: '22:00',
                  EndTime: '02:00',
                }),
                Enabled: value,
              })
            }
            data-cy="change-window-toggle"
          />
        </div>
      </div>

      {formData.changeWindow?.Enabled && (
        <>
          <div className="form-group">
            <div className="col-sm-12">
              <TimeWindowPickerInputGroup
                values={formData.changeWindow}
                onChangeTimeZone={onChangeTimeZone || (() => {})}
                onChangeChangeWindow={(changeWindow) =>
                  onChange('changeWindow', changeWindow)
                }
                timeZone={timeZone}
              />
            </div>
          </div>
          <div className="form-group">
            <div className="col-sm-12">
              <span className="text-warning small flex gap-1 align-top">
                <div className="icon icon-sm">
                  <i className="fa fa-exclamation-triangle" />
                </div>
                <div>
                  GitOps updates to stacks or applications outside the defined
                  change window will not occur.
                </div>
              </span>
            </div>
          </div>
        </>
      )}

      {/* Resources and Metrics */}
      <FormSectionTitle>Resources and Metrics</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              By DISABLING resource over-commit (highly recommended), you can
              ONLY assign namespaces CPU and memory resources that are less (in
              aggregate) than the cluster total minus any system resource
              reservation.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="orange" inline={false}>
            <p>
              By ENABLING resource over-commit, you can assign namespaces more
              resources than are physically available in the cluster. This may
              lead to unexpected deployment failures if there are insufficient
              resources to service demand.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Allow resource over-commit"
            tooltip="Allow resource over-commit"
            labelClass="col-sm-5 col-lg-4"
            checked={formData.enableResourceOverCommit || false}
            onChange={(value) => onChange('enableResourceOverCommit', value)}
            data-cy="resource-over-commit-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              Enabling the metrics feature allows users to use horizontal pod
              autoscaling and to see container and node resource usage. This
              requires metrics server or prometheus to be running in your
              cluster.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip inline={false}>
            <p>
              On any subsequent disabling of the feature, existing deployed
              applications with autoscaling will still autoscale (you would have
              to remove their autoscaler definitions to stop this).
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Enable features using the metrics API"
            tooltip="Enable features using the metrics API"
            labelClass="col-sm-5 col-lg-4"
            checked={formData.useServerMetrics || false}
            onChange={(value) => onChange('useServerMetrics', value)}
            data-cy="server-metrics-toggle"
          />
        </div>
      </div>

      {/* Available storage options */}
      <FormSectionTitle>Available storage options</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              Select which storage options will be available for use when
              deploying applications. Have a look at your storage driver
              documentation to figure out which access policy to configure and
              if the volume expansion capability is supported.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip inline={false}>
            <p>
              You can find more information about access modes in the{' '}
              <a
                href="https://kubernetes.io/docs/concepts/storage/persistent-volumes/#access-modes"
                target="_blank"
                rel="noopener noreferrer"
              >
                official Kubernetes documentation
              </a>
              .
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <InputList
            label="Storage Classes"
            value={storageClasses}
            onChange={(value) => onChange('storageClasses', value)}
            item={StorageClassItem}
            itemBuilder={() => ({ name: '', allowVolumeExpansion: false })}
            addLabel="Add storage class"
            tooltip="Define storage classes and configure volume expansion for each"
            data-cy="storage-classes-list"
          />
        </div>
      </div>
    </>
  );
}

function IngressControllerItem({
  item,
  onChange,
  index,
}: ItemProps<IngressController>) {
  return (
    <div className="flex items-center">
      <div className="max-w-xs">
        <Input
          type="text"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Ingress controller name"
          data-cy={`ingress-controller-name-${index}`}
        />
      </div>
    </div>
  );
}

function StorageClassItem({ item, onChange, index }: ItemProps<StorageClass>) {
  return (
    <div className="flex items-center gap-4">
      <div className="max-w-xs">
        <Input
          type="text"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Storage class name"
          data-cy={`storage-class-name-${index}`}
        />
      </div>
      <div className="flex-shrink-0">
        <SwitchField
          label="Allow volume expansion"
          labelClass="!w-auto"
          checked={item.allowVolumeExpansion}
          onChange={(value) =>
            onChange({ ...item, allowVolumeExpansion: value })
          }
          data-cy={`storage-class-expansion-${index}`}
        />
      </div>
    </div>
  );
}
